import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, X, AlertTriangle, Phone, MessageCircle, ShieldCheck, Loader2 } from 'lucide-react';
import { axiosInstance } from '../../App';
import { toast } from 'sonner';

const FulfillmentBlockedModal = ({ open, onClose, commitment, currentUser, onVerificationComplete }) => {
  const [verifying, setVerifying] = useState(false);

  if (!commitment) return null;

  const formatDateTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const needsTLVerification = !commitment.commitment_tl_verified;
  const needsManagerVerification = !commitment.commitment_mgr_verified;

  // Determine if the current user can verify
  const userRole = currentUser?.role;
  const canVerifyAsTL = needsTLVerification && (userRole === 'team_leader' || userRole === 'admin');
  const canVerifyAsManager = needsManagerVerification && (userRole === 'manager' || userRole === 'admin');

  // Check if the pulse entry is already verified but commitment flags are out of sync
  const tlOutOfSync = commitment.tl_verified && !commitment.commitment_tl_verified;
  const mgrOutOfSync = commitment.mgr_verified && !commitment.commitment_mgr_verified;

  const handleVerifyNow = async () => {
    setVerifying(true);
    try {
      // If pulse is already verified but commitment flags are out of sync,
      // re-trigger verification to sync the flags
      if (canVerifyAsTL || tlOutOfSync) {
        await axiosInstance.post(`/pulse-entries/${commitment.id}/verify-tl`);
      }
      if (canVerifyAsManager || mgrOutOfSync) {
        await axiosInstance.post(`/pulse-entries/${commitment.id}/verify-manager`);
      }
      toast.success('Verification updated successfully');
      if (onVerificationComplete) {
        onVerificationComplete();
      }
      onClose();
    } catch (error) {
      const msg = error.response?.data?.detail || error.message;
      toast.error(`Verification failed: ${msg}`);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-orange-600">
            <AlertTriangle className="w-5 h-5" />
            Fulfillment Blocked
          </DialogTitle>
        </DialogHeader>

        <div className="py-4">
          {/* Clear explanation */}
          <div className="bg-orange-50 border border-orange-200 rounded-md p-3 mb-4">
            <p className="text-sm text-orange-800 font-medium">
              {(tlOutOfSync || mgrOutOfSync)
                ? "Verification flags are out of sync - click 'Sync & Verify' to fix"
                : needsManagerVerification && !needsTLVerification
                ? "Waiting for Manager to verify this commitment"
                : needsTLVerification && !needsManagerVerification
                ? "You need to verify this commitment first"
                : "Both TL and Manager verification required"}
            </p>
          </div>

          <div className="space-y-3 border rounded-md p-4 bg-gray-50">
            <div className="text-sm font-medium text-gray-700 mb-2">Verification Status:</div>

            {/* TL Verification */}
            <div className="flex items-start gap-3">
              {commitment.commitment_tl_verified ? (
                <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              ) : tlOutOfSync ? (
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              ) : (
                <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className="font-medium text-sm">
                  Team Leader Verification
                </div>
                {commitment.commitment_tl_verified ? (
                  <div className="text-xs text-green-600 mt-1">
                    Verified by {commitment.commitment_tl_verified_by || 'TL'}
                    {commitment.commitment_tl_verified_at && (
                      <> at {formatDateTime(commitment.commitment_tl_verified_at)}</>
                    )}
                  </div>
                ) : tlOutOfSync ? (
                  <div className="text-xs text-yellow-600 mt-1 font-medium">
                    Pulse verified by TL but commitment flag not synced - click below to fix
                  </div>
                ) : (
                  <div className="text-xs text-red-600 mt-1 font-medium">
                    Go to Verification page and verify this pulse entry
                  </div>
                )}
              </div>
            </div>

            {/* Manager Verification */}
            <div className="flex items-start gap-3">
              {commitment.commitment_mgr_verified ? (
                <Check className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
              ) : mgrOutOfSync ? (
                <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" />
              ) : (
                <X className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
              )}
              <div className="flex-1">
                <div className="font-medium text-sm">
                  Manager Verification
                </div>
                {commitment.commitment_mgr_verified ? (
                  <div className="text-xs text-green-600 mt-1">
                    Verified by {commitment.commitment_mgr_verified_by || 'Manager'}
                    {commitment.commitment_mgr_verified_at && (
                      <> at {formatDateTime(commitment.commitment_mgr_verified_at)}</>
                    )}
                  </div>
                ) : mgrOutOfSync ? (
                  <div className="text-xs text-yellow-600 mt-1 font-medium">
                    Pulse verified by Manager but commitment flag not synced - click below to fix
                  </div>
                ) : (
                  <div className="text-xs text-red-600 mt-1 font-medium">
                    Pending - Contact your Manager for verification
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Out-of-sync notice */}
          {(tlOutOfSync || mgrOutOfSync) && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm font-medium text-yellow-800 mb-2">Out of Sync Detected:</p>
              <p className="text-xs text-yellow-700">
                This is an older entry where the pulse was verified before the commitment verification system was added.
                Click <strong>Sync & Verify</strong> below to fix the flags and unblock fulfillment.
              </p>
            </div>
          )}

          {/* Verify Now button for users who can verify */}
          {(canVerifyAsTL || canVerifyAsManager || tlOutOfSync || mgrOutOfSync) && (
            <div className="mt-4">
              <Button
                onClick={handleVerifyNow}
                disabled={verifying}
                className="w-full bg-green-600 hover:bg-green-700 text-white"
              >
                {verifying ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    {(tlOutOfSync || mgrOutOfSync) ? 'Sync & Verify' : 'Verify Now'}
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Action instructions - only show when user cannot verify themselves */}
          {needsManagerVerification && !needsTLVerification && !canVerifyAsManager && !mgrOutOfSync && (
            <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-sm font-medium text-blue-800 mb-2">What to do next:</p>
              <ul className="text-xs text-blue-700 space-y-1">
                <li className="flex items-center gap-2">
                  <Phone className="w-3 h-3" />
                  Call or message your Manager to verify
                </li>
                <li className="flex items-center gap-2">
                  <MessageCircle className="w-3 h-3" />
                  The Manager can verify from their Command Center
                </li>
              </ul>
            </div>
          )}

          {needsTLVerification && !canVerifyAsTL && !tlOutOfSync && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
              <p className="text-sm font-medium text-yellow-800 mb-2">Action Required:</p>
              <p className="text-xs text-yellow-700">
                Go to the <strong>Verification</strong> page in the sidebar to verify this pulse entry first.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FulfillmentBlockedModal;
